import React, { useState, useEffect, useContext } from 'react';
import { api } from '../../utils/requestInterceptor';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import { useNavigate } from 'react-router-dom';
import ViewsNavigation from './ViewsNavigation';
import { AuthContext } from '../../contexts/AuthContext';
import { useRaciAssignments } from '../../hooks/useRaciAssignments';
import { createCalendarDate, getDueDateStatusColor } from '../../utils/dateUtils';

const locales = {
  'en-US': require('date-fns/locale/en-US')
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales
});


// Get initials from a user's name
const getInitials = (name) => {
  if (!name) return '';
  
  const names = name.split(' ');
  if (names.length === 1) return names[0].charAt(0).toUpperCase();
  
  return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
};

// User colors for consistent color coding
const userColors = {};
const getColorForUser = (userId) => {
  if (userColors[userId]) return userColors[userId];
  
  // Generate a consistent color based on the userId
  const colorOptions = [
    '#3B82F6', // blue-500
    '#8B5CF6', // violet-500
    '#EC4899', // pink-500
    '#14B8A6', // teal-500
    '#F59E0B', // amber-500
    '#6366F1', // indigo-500
    '#10B981', // emerald-500
    '#06B6D4', // cyan-500
    '#A855F7', // purple-500
    '#F43F5E', // rose-500
  ];
  
  // Deterministic selection based on userId
  const index = parseInt(userId) % colorOptions.length;
  userColors[userId] = colorOptions[index];
  
  return userColors[userId];
};

function InformedProjects() {
  const { currentUser } = useContext(AuthContext);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('month');
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('all');
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [calendarView, setCalendarView] = useState('month');
  const navigate = useNavigate();
  const { getBatchAssignments } = useRaciAssignments();

  // Set the default user filter to current user when component mounts
  useEffect(() => {
    // Only set if currentUser exists and selectedUserId hasn't been explicitly changed
    if (currentUser && currentUser.id && selectedUserId === 'all') {
      setSelectedUserId(currentUser.id.toString());
    }
  }, [currentUser, selectedUserId]);

  // Fetch departments
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await api.get('/departments');
        if (response.data && response.data.departments) {
          setDepartments(response.data.departments);
        }
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    };
    
    fetchDepartments();
  }, []);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get('/auth/users');
        if (response.data && response.data.users) {
          setUsers(response.data.users);
          
          // Set default user filter to current user if logged in
          if (currentUser && currentUser.id && selectedUserId === 'all') {
            setSelectedUserId(currentUser.id.toString());
          }
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    
    fetchUsers();
  }, [currentUser, selectedUserId]);

  // Fetch projects where the current user is informed
  useEffect(() => {
    const fetchProjects = async () => {
      if (!currentUser?.id) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Fetch all projects first
        const projectsResponse = await api.get('/projects');
        const allProjects = projectsResponse.data.projects || [];
        
        // Filter to projects with due dates and exclude completed/canceled projects
        const projectsWithDueDates = allProjects
          .filter(project => project.due_date)
          .filter(project => {
            const status = project.status ? project.status.toLowerCase() : '';
            return !status.includes('complete') && 
                   !status.includes('finished') && 
                   !status.includes('done') && 
                   !status.includes('cancel') && 
                   !status.includes('abandon');
          });
        
        // Get all project IDs with due dates
        const projectIds = projectsWithDueDates.map(project => project.id);
        
        // Fetch all assignments for these projects in a single batch call
        const assignments = await getBatchAssignments('project', projectIds);
        
        console.log('Batch assignments:', assignments);
        
        // Filter to keep only projects where the user is informed
        const informedProjects = projectsWithDueDates.filter(project => {
          // Check if this project exists in assignments and if the user is informed
          if (!assignments[project.id]) return false;
          
          const projectAssignments = assignments[project.id].project?.assignments?.I || [];
          
          // If filtering by a specific user (not the current user), only show projects where that user is informed
          if (selectedUserId !== 'all' && parseInt(selectedUserId) !== currentUser.id) {
            return projectAssignments.some(a => a.user_id === parseInt(selectedUserId));
          }
          
          // Otherwise, show projects where the current user is informed
          return projectAssignments.some(a => a.user_id === currentUser.id);
        });
        
        console.log('Informed projects:', informedProjects);
        
        // Filter projects by department and status
        const filteredProjects = informedProjects.filter(project => {
          // Filter by department if selected
          if (selectedDepartmentId !== 'all' && project.department_id !== parseInt(selectedDepartmentId)) {
            return false;
          }
          
          // Filter by status if selected
          if (selectedStatus !== 'all' && project.status !== selectedStatus) {
            return false;
          }
          
          return true;
        });
        
        // Convert to calendar events
        const calendarEvents = filteredProjects.map(project => {
          // Get informed users for display
          const informedAssignments = assignments[project.id].project?.assignments?.I || [];
          
          // Only include specific user if filtering, otherwise include all informed users
          const informedUserIds = selectedUserId !== 'all' 
            ? informedAssignments.filter(a => a.user_id === parseInt(selectedUserId)).map(a => a.user_id)
            : informedAssignments.map(a => a.user_id);
          
          const informedUserNames = informedUserIds.map(userId => {
            const user = users.find(u => u.id === userId);
            return user ? user.name : 'Unknown';
          });
          
          // Get initials for display
          const initialsDisplay = informedUserIds.length === 0 
            ? '[NONE] ' 
            : `[${informedUserNames.map(name => getInitials(name)).join(', ')}] `;
          
          const eventDate = createCalendarDate(project.due_date);
          return {
            id: `project-${project.id}`,
            title: `${initialsDisplay}${project.name}`,
            start: eventDate,
            end: eventDate,
            allDay: true,
            resource: {
              project,
              informedUserIds,
              dueDateColor: getDueDateStatusColor(project.due_date)
            }
          };
        });
        
        setEvents(calendarEvents);
      } catch (error) {
        console.error('Error fetching informed projects:', error);
        setError('Failed to load projects for calendar view');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProjects();
  }, [currentUser?.id, getBatchAssignments, selectedDepartmentId, selectedUserId, selectedStatus, users]);

  // Style event based on due date status and user
  const eventStyleGetter = (event) => {
    const resource = event.resource || {};
    const dueDateColor = resource.dueDateColor || '#6B7280'; // gray-500 default
    
    // Get the color for the first informed user (if any)
    const userColor = resource.informedUserIds && resource.informedUserIds.length > 0
      ? getColorForUser(resource.informedUserIds[0])
      : '#4B5563'; // gray-600 default
    
    return {
      style: {
        backgroundColor: userColor,
        borderLeft: `4px solid ${dueDateColor}`,
        color: 'white',
        borderRadius: '4px',
        display: 'block',
        padding: '2px 5px'
      }
    };
  };

  // Handle click on event to navigate to project
  const handleEventClick = (event) => {
    const resource = event.resource;
    if (resource && resource.project) {
      navigate(`/projects/${resource.project.id}`);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#111827', borderRadius: '8px', padding: '20px' }}>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-white">Informed Projects</h1>
          <p className="mt-2 text-sm text-gray-300">
            Calendar view of projects where you are marked as Informed.
          </p>
        </div>
      </div>
      
      <ViewsNavigation />
      
      {/* Filters */}
      <div className="mt-6 mb-6 grid grid-cols-1 md:grid-cols-4 gap-4 bg-gray-800 p-4 rounded-lg border border-gray-700">
        <div>
          <label htmlFor="department-filter" className="block text-sm font-medium text-gray-300 mb-1">
            Filter by Department
          </label>
          <select
            id="department-filter"
            value={selectedDepartmentId}
            onChange={(e) => setSelectedDepartmentId(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="all">All Departments</option>
            {departments.map((department) => (
              <option key={department.id} value={department.id}>
                {department.name}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label htmlFor="user-filter" className="block text-sm font-medium text-gray-300 mb-1">
            Filter by User
          </label>
          <select
            id="user-filter"
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="all">All Users</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label htmlFor="status-filter" className="block text-sm font-medium text-gray-300 mb-1">
            Filter by Status
          </label>
          <select
            id="status-filter"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="all">All Statuses</option>
            <option value="Not Started">Not Started</option>
            <option value="In Progress">In Progress</option>
            <option value="Completed">Completed</option>
            <option value="On Hold">On Hold</option>
          </select>
        </div>
        
        <div>
          <label htmlFor="calendar-view" className="block text-sm font-medium text-gray-300 mb-1">
            Calendar View
          </label>
          <div className="mt-1 flex rounded-md shadow-sm">
            <button
              type="button"
              onClick={() => setCalendarView('month')}
              className={`relative inline-flex items-center px-4 py-2 rounded-l-md border border-gray-300 ${
                calendarView === 'month' 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              } text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500`}
            >
              Month
            </button>
            <button
              type="button"
              onClick={() => setCalendarView('week')}
              className={`relative inline-flex items-center px-4 py-2 border-t border-b border-gray-300 ${
                calendarView === 'week' 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              } text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500`}
            >
              Week
            </button>
            <button
              type="button"
              onClick={() => setCalendarView('day')}
              className={`relative inline-flex items-center px-4 py-2 rounded-r-md border border-gray-300 ${
                calendarView === 'day' 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              } text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500`}
            >
              Day
            </button>
          </div>
        </div>
      </div>
      
      {/* Legend */}
      <div className="mb-4 flex flex-wrap gap-4 bg-gray-700 p-4 rounded-md border border-gray-600">
        <div className="flex items-center text-sm text-gray-300">
          <span className="mr-2 h-3 w-3 rounded-full bg-blue-500"></span>
          <span>Not Started</span>
        </div>
        <div className="flex items-center text-sm text-gray-300">
          <span className="mr-2 h-3 w-3 rounded-full bg-amber-500"></span>
          <span>In Progress</span>
        </div>
        <div className="flex items-center text-sm text-gray-300">
          <span className="mr-2 h-3 w-3 rounded-full bg-green-500"></span>
          <span>Completed</span>
        </div>
        <div className="flex items-center text-sm text-gray-300">
          <span className="mr-2 h-3 w-3 rounded-full bg-gray-400"></span>
          <span>On Hold</span>
        </div>
      </div>

      {/* Calendar */}
      <div className="mt-4">
        <div className="text-lg font-medium text-white mb-2">
          Projects Calendar
        </div>
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 p-4 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gray-800 p-4 rounded-md shadow-sm border border-gray-700 h-[700px]">
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%' }}
              views={['month', 'week', 'day']}
              view={calendarView}
              onView={setCalendarView}
              eventPropGetter={eventStyleGetter}
              onSelectEvent={handleEventClick}
              popup
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default InformedProjects; 